import { useState, useRef, useEffect } from 'react'
import type { JobApplication } from '@/core/types'
import { Calendar, ChevronDown, Download, ExternalLink } from 'lucide-react'
import {
  createInterviewEventOptions,
  generateICSContent,
  downloadICSFile,
  buildGoogleCalendarUrl,
} from '@/core/utils/calendar'

interface Props {
  application: JobApplication
}

export function CalendarEventButton({ application }: Props) {
  const [open, setOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const eventOptions = createInterviewEventOptions(application)

  // Close dropdown on outside click
  useEffect(() => {
    if (!open) return
    function handleClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  if (!eventOptions) return null

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setOpen(prev => !prev)}
        className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
      >
        <Calendar className="w-3.5 h-3.5" />
        Add to Calendar
        <ChevronDown className="w-3.5 h-3.5" />
      </button>

      {open && (
        <div className="absolute right-0 mt-1 w-52 bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-1">
          <button
            onClick={() => {
              const safeName = application.company.replace(/[^a-zA-Z0-9]/g, '_')
              downloadICSFile(generateICSContent(eventOptions), `interview_${safeName}.ics`)
              setOpen(false)
            }}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <Download className="w-4 h-4 text-gray-400" />
            Download .ics file
          </button>
          <button
            onClick={() => {
              window.open(buildGoogleCalendarUrl(eventOptions), '_blank', 'noopener,noreferrer')
              setOpen(false)
            }}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <ExternalLink className="w-4 h-4 text-gray-400" />
            Open Google Calendar
          </button>
        </div>
      )}
    </div>
  )
}
